import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { logError, logInfo } from '../config_and_helpers/logger';
import { isCanceledRequest } from '../utils/listSearchRequest';

const LOG_CONTEXT = 'shop_client';

/**
 * @param {Object|Array|string|null} errPayload
 * @returns {string[]}
 */
export const formatShopErrors = errPayload => {
  if (errPayload == null) {
    return ['Request failed. Please try again.'];
  }
  if (typeof errPayload === 'string') {
    return [errPayload];
  }
  if (Array.isArray(errPayload)) {
    return errPayload.map(item => String(item));
  }
  if (errPayload.detail) {
    return [String(errPayload.detail)];
  }
  const messages = [];
  Object.entries(errPayload).forEach(([key, value]) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach(v => messages.push(`${key}: ${v}`));
    } else {
      messages.push(String(value));
    }
  });
  return messages.length ? messages : ['Request failed. Please try again.'];
};

/**
 * GET /v1/shop/catalog/ — merged multi-lab shop catalog (lab users only).
 * Response is a plain JSON array in v1 (no DRF pagination).
 *
 * @async
 * @param {string} token
 * @param {Object} [options]
 * @param {string} [options.catNum] - Optional exact catalog number filter.
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{ success: boolean, data?: Array, messages?: string[], status?: number }>}
 */
export const getShopCatalog = async (token, options = {}) => {
  const { catNum, signal } = options;
  let url = `${BACKEND_URL}shop/catalog/`;
  const trimmedCatNum = String(catNum ?? '').trim();
  if (trimmedCatNum) {
    const params = new URLSearchParams({ cat_num: trimmedCatNum });
    url = `${url}?${params.toString()}`;
  }

  try {
    logInfo(LOG_CONTEXT, 'getShopCatalog start', {
      hasCatNumFilter: Boolean(trimmedCatNum),
    });
    const response = await axios.get(url, {
      headers: { Authorization: `Token ${token}` },
      signal,
    });
    const data = Array.isArray(response.data) ? response.data : [];
    logInfo(LOG_CONTEXT, 'getShopCatalog success', { count: data.length });
    return { success: true, data };
  } catch (error) {
    if (isCanceledRequest(error)) {
      return { success: false, canceled: true };
    }
    logError(LOG_CONTEXT, 'getShopCatalog failed', error.response?.data ?? error);
    return {
      success: false,
      messages: formatShopErrors(error.response?.data),
      status: error.response?.status,
    };
  }
};
